// src/components/Eventos/DownloadHorizontalLffButton.js

import React from 'react';
import { Button } from '@mui/material';

/**
 * Descarga un archivo .lff para eventos de campo horizontal,
 * asignando las columnas tal como se ve en HorizontalEvents:
 *  - flightPlace    = participant.lane  (columna "Carril")
 *  - athleteId      = participant.id    (columna "Número")
 *  - competePosition= participant.place (columna "Lugar")
 *  - eventPlace     = participant.place (columna "Lugar")
 */
const DownloadHorizontalLffButton = ({
  heatDetails,
  rankings,
  getParticipantResults,
  measurementSystem = 'Metric',
}) => {

  // Formatear el heat con dos dígitos (ej. "01")
  const formatHeat = (heat) => String(heat).padStart(2, '0');

  const handleDownloadLff = () => {
    // 1) Encabezado de la primera línea
    //    event#, round#, flight#, eventName, measurementSystem
    const headerLine = [
      heatDetails.eventNumber || '',
      heatDetails.round || '',
      formatHeat(heatDetails.heat),
      heatDetails.eventName || '',
      measurementSystem,
    ].join(',');

    // Preparamos la lista de participantes y, si quieres, rankings
    const participants = heatDetails.participants || [];

    // 2) Generar cada línea de competidor:
    const competitorLines = participants.map((participant, index) => {
      const results = getParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        participant.id
      ) || {};
    
      // Obtener estado especial si existe
      const specialStatus = results.special && results.special !== '-' ? results.special : '';
    
      // flightPlace = carril
      const flightPlace = participant.lane ?? '';
    
      // athleteId = número
      const athleteId = participant.id ?? '';
    
      // competePosition = lugar (priorizar estado especial)
      const competePosition = specialStatus || participant.place || '';
    
      // eventPlace = lugar (priorizar estado especial)
      const eventPlace = specialStatus || participant.place || '';

      // lastName / firstName a partir de fullName
      const nameParts = (participant.fullName || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Afiliación (equipo)
      const affiliation = participant.affiliation || '';

      
      const attempts = Array.isArray(results.attempts) ? results.attempts : [];

      // Convertir cada intento en (mark, wind)
      const attemptsData = attempts.map((att) => {
        let mark = att.result ?? '';
        let wind = att.wind ?? '';

        // Convertir "X" => "F"
        if (typeof mark === 'string' && mark.toUpperCase() === 'X') {
          mark = 'F';
        }
        // Eliminar la "m" final en caso de "2.36m"
        else if (typeof mark === 'string' && mark.endsWith('m')) {
          mark = mark.slice(0, -1);
        }

        return [mark, wind];
      });

      // Aplanar: [[mark1, wind1], [mark2, wind2]] => [mark1, wind1, mark2, wind2]
      const attemptsFlat = attemptsData.flat();

      // flight place, athlete ID, compete position, event place, lastName, firstName, affiliation, ...
      const lineArray = [
        flightPlace,
        athleteId,
        competePosition,
        eventPlace,
        lastName,
        firstName,
        affiliation,
        ...attemptsFlat,
      ];

      return lineArray.join(',');
    });

    // 3) Unir con \r\n y crear el archivo para descargar
    const fileContent = [headerLine, ...competitorLines].join('\r\n');
    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Nombre del archivo
    const fileName = `${heatDetails.eventNumber}-${heatDetails.round}-${formatHeat(
      heatDetails.heat
    )}.lff`;

    // Dispara la descarga
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="contained" color="primary" onClick={handleDownloadLff}>
      Descargar archivo .lff
    </Button>
  );
};

export default DownloadHorizontalLffButton;
